/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keysOf } from '../common';
import { EuiLoadingSpinner } from '../loading';
import { EuiResizeObserver } from '../observer/resize_observer';
import { EuiText } from '../text';
import { EuiI18n } from '../i18n';
import { htmlIdGenerator, withEuiTheme } from '../../services';
import { EuiButtonIcon } from '../button';
import { euiAccordionButtonStyles, euiAccordionChildrenStyles, euiAccordionChildWrapperStyles, euiAccordionIconButtonStyles, euiAccordionOptionalActionStyles, euiAccordionSpinnerStyles, euiAccordionTriggerWrapperStyles } from './accordion.styles';
var paddingSizeToClassNameMap = {
  none: '',
  xs: 'euiAccordion__padding--xs',
  s: 'euiAccordion__padding--s',
  m: 'euiAccordion__padding--m',
  l: 'euiAccordion__padding--l',
  xl: 'euiAccordion__padding--xl'
};
export var PADDING_SIZES = keysOf(paddingSizeToClassNameMap);
export class EuiAccordionClass extends Component {
  static defaultProps = {
    initialIsOpen: false,
    paddingSize: 'none',
    arrowDisplay: 'left',
    isLoading: false,
    isLoadingMessage: false,
    element: 'div',
    buttonElement: 'button'
  };
  childContent = null;
  childWrapper = null;
  state = {
    isOpen: this.props.forceState ? this.props.forceState === 'open' : this.props.initialIsOpen
  };
  setChildContentHeight = () => {
    const {
      forceState
    } = this.props;
    requestAnimationFrame(() => {
      const height = this.childContent && (forceState ? forceState === 'open' : this.state.isOpen) ? this.childContent.clientHeight : 0;
      this.childWrapper && this.childWrapper.setAttribute('style', `height: ${height}px`);
    });
  };

  componentDidMount() {
    this.setChildContentHeight();
  }

  componentDidUpdate() {
    this.setChildContentHeight();
  }

  onToggle = () => {
    const {
      forceState
    } = this.props;

    if (forceState) {
      this.props.onToggle && this.props.onToggle(forceState === 'open' ? false : true);
    } else {
      this.setState(prevState => ({
        isOpen: !prevState.isOpen
      }), () => {
        if (this.state.isOpen && this.childWrapper) {
          this.childWrapper.focus();
        }

        this.props.onToggle && this.props.onToggle(this.state.isOpen);
      });
    }
  };
  setChildContentRef = node => {
    this.childContent = node;
  };
  generatedId = htmlIdGenerator()();

  render() {
    const {
      children,
      buttonContent,
      className,
      id,
      element: Element = 'div',
      buttonClassName,
      buttonContentClassName,
      extraAction,
      paddingSize,
      initialIsOpen,
      arrowDisplay,
      forceState,
      isLoading,
      isLoadingMessage,
      buttonProps,
      buttonElement: _ButtonElement = 'button',
      arrowProps,
      theme,
      ...rest
    } = this.props;
    const isOpen = forceState ? forceState === 'open' : this.state.isOpen; // Force button element to be a legend if the element is a fieldset

    const ButtonElement = Element === 'fieldset' ? 'legend' : _ButtonElement;
    const buttonElementIsFocusable = ButtonElement === 'button'; // Force visibility of arrow button if button element is not focusable

    const _arrowDisplay = arrowDisplay === 'none' && !buttonElementIsFocusable ? 'left' : arrowDisplay;

    const classes = classNames('euiAccordion', {
      'euiAccordion-isOpen': isOpen
    }, className);
    const paddingClass = paddingSize ? classNames(paddingSizeToClassNameMap[paddingSize]) : undefined;
    const childrenClasses = classNames(paddingClass, {
      'euiAccordion__children-isLoading': isLoading
    });
    const buttonClasses = classNames('euiAccordion__button', buttonClassName, buttonProps?.className);
    const buttonContentClasses = classNames('euiAccordion__buttonContent', buttonContentClassName);
    const iconButtonClasses = classNames('euiAccordion__iconButton', {
      'euiAccordion__iconButton-isOpen': isOpen,
      'euiAccordion__iconButton--right': _arrowDisplay === 'right'
    }, arrowProps?.className); // Emotion styles

    const buttonStyles = euiAccordionButtonStyles(theme);
    const cssButtonStyles = [buttonStyles.euiAccordion__button];
    const childrenStyles = euiAccordionChildrenStyles(theme);
    const cssChildrenStyles = [childrenStyles.euiAccordion__children, isLoading && childrenStyles.isLoading, paddingSize === 'none' ? undefined : childrenStyles[paddingSize]];
    const childWrapperStyles = euiAccordionChildWrapperStyles(theme);
    const cssChildWrapperStyles = [childWrapperStyles.euiAccordion__childWrapper, isOpen && childWrapperStyles.isOpen];
    const iconButtonStyles = euiAccordionIconButtonStyles(theme);
    const cssIconButtonStyles = [iconButtonStyles.euiAccordion__iconButton, isOpen && iconButtonStyles.isOpen, _arrowDisplay === 'right' && iconButtonStyles.arrowRight];
    const optionalActionStyles = euiAccordionOptionalActionStyles();
    const cssOptionalActionStyles = [optionalActionStyles.euiAccordion__optionalAction];
    const spinnerStyles = euiAccordionSpinnerStyles(theme);
    const cssSpinnerStyles = [spinnerStyles.euiAccordion__spinner];
    const triggerWrapperStyles = euiAccordionTriggerWrapperStyles();
    const cssTriggerWrapperStyles = [triggerWrapperStyles.euiAccordion__triggerWrapper];
    let iconButton;
    const buttonId = buttonProps?.id ?? this.generatedId;

    if (_arrowDisplay !== 'none') {
      iconButton = <EuiButtonIcon color="text" {...arrowProps} className={iconButtonClasses} css={cssIconButtonStyles} iconType="arrowRight" onClick={this.onToggle} aria-controls={id} aria-expanded={isOpen} aria-labelledby={buttonId} tabIndex={buttonElementIsFocusable ? -1 : 0} />;
    }

    let optionalAction = null;

    if (isLoading || extraAction) {
      optionalAction = <div className="euiAccordion__optionalAction" css={cssOptionalActionStyles}>
          {isLoading ? <EuiLoadingSpinner /> : extraAction}
        </div>;
    }

    let childrenContent;

    if (isLoading && isLoadingMessage) {
      childrenContent = <>
          <EuiLoadingSpinner className="euiAccordion__spinner" css={cssSpinnerStyles} />
          <EuiText size="s">
            <p>
              {isLoadingMessage && isLoadingMessage !== true ? isLoadingMessage : <EuiI18n token="euiAccordion.isLoading" default="Loading" />}
            </p>
          </EuiText>
        </>;
    } else {
      childrenContent = children;
    }

    const button = <ButtonElement {...buttonProps} id={buttonId} className={buttonClasses} css={cssButtonStyles} aria-controls={id} aria-expanded={isOpen} onClick={this.onToggle} type={ButtonElement === 'button' ? 'button' : undefined}>
        <span className={buttonContentClasses}>{buttonContent}</span>
      </ButtonElement>;
    return <Element className={classes} {...rest}>
        <div className="euiAccordion__triggerWrapper" css={cssTriggerWrapperStyles}>
          {_arrowDisplay === 'left' && iconButton}
          {button}
          {optionalAction}
          {_arrowDisplay === 'right' && iconButton}
        </div>

        <div className="euiAccordion__childWrapper" css={cssChildWrapperStyles} ref={node => {
        this.childWrapper = node;
      }} tabIndex={-1} role="region" aria-labelledby={buttonId} id={id}>
          <EuiResizeObserver onResize={this.setChildContentHeight}>
            {resizeRef => <div ref={ref => {
            this.setChildContentRef(ref);
            resizeRef(ref);
          }}>
                <div className={childrenClasses} css={cssChildrenStyles}>
                  {childrenContent}
                </div>
              </div>}
          </EuiResizeObserver>
        </div>
      </Element>;
  }

}
EuiAccordionClass.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  id: PropTypes.string.isRequired,

  /**
       * Applied to the entire .euiAccordion wrapper.
       * When using `fieldset`, it will enforce `buttonElement = legend` as well.
       */
  element: PropTypes.oneOf(["div", "fieldset"]),

  /**
       * Class that will apply to the trigger for the accordion.
       */
  buttonClassName: PropTypes.string,

  /**
       * Apply more props to the triggering button
       */
  buttonProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string
  }),

  /**
       * Class that will apply to the trigger content for the accordion.
       */
  buttonContentClassName: PropTypes.string,

  /**
       * The content of the clickable trigger
       */
  buttonContent: PropTypes.node,

  /**
       * Applied to the main button receiving the `onToggle` event.
       * Anything other than the default `button` does not support removing the arrow display (for accessibility of focus).
       */
  buttonElement: PropTypes.oneOf(["div", "legend", "button"]),

  /**
       * Extra props to pass to the EuiButtonIcon containing the arrow.
       */
  arrowProps: PropTypes.any,

  /**
       * Will appear right aligned against the button. Useful for separate actions like deletions.
       */
  extraAction: PropTypes.node,

  /**
       * The accordion will start in the open state.
       */
  initialIsOpen: PropTypes.bool,

  /**
       * Optional callback method called on open and close with a single `isOpen` parameter
       */
  onToggle: PropTypes.func,

  /**
       * The padding around the exposed accordion content.
       */
  paddingSize: PropTypes.oneOf(["none", "xs", "s", "m", "l", "xl"]),

  /**
       * Placement of the arrow indicator, or 'none' to hide it.
       */
  arrowDisplay: PropTypes.oneOf(["left", "right", "none"]),

  /**
       * Control the opening of accordion via prop
       */
  forceState: PropTypes.oneOf(["closed", "open"]),

  /**
       * Change `extraAction` and children into a loading spinner
       */
  isLoading: PropTypes.bool,

  /**
       * Choose whether the loading message replaces the content. Customize the message by passing a node
       */
  isLoadingMessage: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.node.isRequired])
};
export const EuiAccordion = withEuiTheme(EuiAccordionClass);

try {
  EuiAccordionClass.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/accordion/accordion.tsx',
    description: '',
    displayName: 'EuiAccordionClass',
    methods: [],
    props: {
      theme: {
        defaultValue: null,
        description: '',
        name: 'theme',
        parent: {
          fileName: 'app/src/services/theme/hooks.tsx',
          name: 'WithEuiThemeProps'
        },
        declarations: [{
          fileName: 'app/src/services/theme/hooks.tsx',
          name: 'WithEuiThemeProps'
        }],
        required: true,
        type: {
          name: 'UseEuiTheme<{}>'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      element: {
        defaultValue: {
          value: "'div' as const"
        },
        description: 'Applied to the entire .euiAccordion wrapper.\n' + 'When using `fieldset`, it will enforce `buttonElement = legend` as well.',
        name: 'element',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"div" | "fieldset"',
          value: [{
            value: '"div"'
          }, {
            value: '"fieldset"'
          }]
        }
      },
      buttonClassName: {
        defaultValue: null,
        description: 'Class that will apply to the trigger for the accordion.',
        name: 'buttonClassName',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      buttonProps: {
        defaultValue: null,
        description: 'Apply more props to the triggering button',
        name: 'buttonProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'CommonProps & HTMLAttributes<HTMLElement>'
        }
      },
      buttonContentClassName: {
        defaultValue: null,
        description: 'Class that will apply to the trigger content for the accordion.',
        name: 'buttonContentClassName',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      buttonContent: {
        defaultValue: null,
        description: 'The content of the clickable trigger',
        name: 'buttonContent',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      buttonElement: {
        defaultValue: {
          value: "'button' as const"
        },
        description: 'Applied to the main button receiving the `onToggle` event.\n' + 'Anything other than the default `button` does not support removing the arrow display (for accessibility of focus).',
        name: 'buttonElement',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"button" | "div" | "legend"',
          value: [{
            value: '"button"'
          }, {
            value: '"div"'
          }, {
            value: '"legend"'
          }]
        }
      },
      arrowProps: {
        defaultValue: null,
        description: 'Extra props to pass to the EuiButtonIcon containing the arrow.',
        name: 'arrowProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Partial<Omit<EuiButtonIconProps, "aria-labelledby" | "onClick" | "iconType">>'
        }
      },
      extraAction: {
        defaultValue: null,
        description: 'Will appear right aligned against the button. Useful for separate actions like deletions.',
        name: 'extraAction',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      initialIsOpen: {
        defaultValue: {
          value: 'false'
        },
        description: 'The accordion will start in the open state.',
        name: 'initialIsOpen',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onToggle: {
        defaultValue: null,
        description: 'Optional callback method called on open and close with a single `isOpen` parameter',
        name: 'onToggle',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(isOpen: boolean) => void'
        }
      },
      paddingSize: {
        defaultValue: {
          value: "'none' as const"
        },
        description: 'The padding around the exposed accordion content.',
        name: 'paddingSize',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"none" | "xs" | "s" | "m" | "l" | "xl"',
          value: [{
            value: '"none"'
          }, {
            value: '"xs"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }]
        }
      },
      arrowDisplay: {
        defaultValue: {
          value: "'left' as const"
        },
        description: "Placement of the arrow indicator, or 'none' to hide it.",
        name: 'arrowDisplay',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"none" | "left" | "right"',
          value: [{
            value: '"none"'
          }, {
            value: '"left"'
          }, {
            value: '"right"'
          }]
        }
      },
      forceState: {
        defaultValue: null,
        description: 'Control the opening of accordion via prop',
        name: 'forceState',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"closed" | "open"',
          value: [{
            value: '"closed"'
          }, {
            value: '"open"'
          }]
        }
      },
      isLoading: {
        defaultValue: {
          value: 'false'
        },
        description: 'Change `extraAction` and children into a loading spinner',
        name: 'isLoading',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoadingMessage: {
        defaultValue: {
          value: 'false'
        },
        description: 'Choose whether the loading message replaces the content. Customize the message by passing a node',
        name: 'isLoadingMessage',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      }
    },
    extendedInterfaces: ['WithEuiThemeProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'Attributes']
  };
} catch (e) {}

try {
  EuiAccordion.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/accordion/accordion.tsx',
    description: '',
    displayName: 'EuiAccordion',
    methods: [],
    props: {
      ref: {
        defaultValue: null,
        description: '',
        name: 'ref',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'Ref<unknown>'
        }
      }
    },
    extendedInterfaces: ['WithEuiThemeProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'Attributes']
  };
} catch (e) {}